import { InspectionTemplateItemModel } from 'components/dataProviders/withRequiredInspectionTemplate/types';
import type { InspectionTemplateModel } from 'shared/domain/template/inspectionTemplateModel';
import { EntityState, isEntityStateArchived } from 'components/common/dataGrid/columnsFactory/types';
import { Identificable } from 'shared/types/commonView';
import { getInspectionStoreState } from 'store/inspection';
import { NavigateFunction } from 'react-router-dom';
import { APP_PATHS } from 'setup/navigation/paths';
import { TemplateListError } from '../types';
import { NOT_FOUND_ERROR, NO_TEMPLATES_ERROR } from './errors';

export function canFindOnTemplatesList(
  templates: Identificable[],
  selected: string
): boolean {
  return templates.some((template) => template._id === selected);
}

export interface DetermineActiveTemplateResult {
  templateId: string | undefined;
  shouldScroll: boolean;
  error: TemplateListError | undefined;
  shouldUpdateUrl: boolean;
}

function isTemplateKnown(
  templateId: string,
  cardList: InspectionTemplateItemModel[],
  zustandEntities: InspectionTemplateModel[],
  listState: EntityState
): boolean {
  if (cardList.some((t) => t._id === templateId)) {
    return true;
  }

  // Must match the CURRENTLY VISIBLE filter (archived vs published), not just
  // "not deleted" - otherwise a template that just left the current filter
  // (e.g. restored while browsing the archived list) is wrongly considered
  // resolvable, `template` never matches `cardList`, and the detail panel
  // spins forever with no error state to fall back to.
  const matchesCurrentFilter = isEntityStateArchived(listState)
    ? (t: InspectionTemplateModel) => Boolean(t.deleted)
    : (t: InspectionTemplateModel) => !t.deleted;

  if (
    zustandEntities.some(
      (t) => t._id === templateId && matchesCurrentFilter(t)
    )
  ) {
    return true;
  }

  // These two checks read a separate store slot (the single "currently
  // loaded template" used by the detail/edit view) that persists regardless
  // of the current list filter - so they must also be filter-aware, or a
  // template that was open before its archived/published state changed (and
  // therefore fell out of `cardList`) would stay "known" forever.
  const state = getInspectionStoreState();
  if (
    state.templateId === templateId &&
    state.loadingTemplate &&
    (!state.inspectionTemplate || matchesCurrentFilter(state.inspectionTemplate))
  ) {
    return true;
  }
  if (
    state.inspectionTemplate?._id === templateId &&
    matchesCurrentFilter(state.inspectionTemplate)
  ) {
    return true;
  }

  return false;
}

function existsRegardlessOfFilter(
  templateId: string,
  cardList: InspectionTemplateItemModel[],
  zustandEntities: InspectionTemplateModel[]
): boolean {
  if (cardList.some((t) => t._id === templateId)) {
    return true;
  }

  if (zustandEntities.some((t) => t._id === templateId)) {
    return true;
  }

  const state = getInspectionStoreState();
  return (
    (state.templateId === templateId && state.loadingTemplate) ||
    state.inspectionTemplate?._id === templateId
  );
}

function tryResolveFromUrl(
  urlId: string | undefined,
  cardList: InspectionTemplateItemModel[],
  zustandEntities: InspectionTemplateModel[],
  listState: EntityState
): DetermineActiveTemplateResult | null {
  if (!urlId) return null;

  if (isTemplateKnown(urlId, cardList, zustandEntities, listState)) {
    return {
      templateId: urlId,
      shouldScroll: true,
      error: undefined,
      shouldUpdateUrl: false,
    };
  }

  // The id might be valid but simply belong to the OTHER filter (e.g. the
  // user switched Published -> Archived while a published template's URL
  // was open). That's not a "not found" situation - the template exists and
  // is accessible, it's just not part of the currently selected view - so
  // fall through to the normal empty/first-item resolution instead of
  // showing the harsh "template doesn't exist / no permission" error.
  if (existsRegardlessOfFilter(urlId, cardList, zustandEntities)) {
    return null;
  }

  // The full template list may still be loading (e.g. a fresh direct URL
  // navigation before the initial fetch resolves) - `zustandEntities` isn't
  // authoritative yet, so don't declare "not found" prematurely. The caller
  // already re-runs this once loading finishes.
  if (getInspectionStoreState().inspectionTemplatesLoading) {
    return null;
  }

  return {
    templateId: undefined,
    shouldScroll: false,
    error: NOT_FOUND_ERROR,
    shouldUpdateUrl: false,
  };
}

function tryResolveFromState(
  stateId: string | undefined,
  cardList: InspectionTemplateItemModel[],
  zustandEntities: InspectionTemplateModel[],
  listState: EntityState
): DetermineActiveTemplateResult | null {
  if (!stateId || !isTemplateKnown(stateId, cardList, zustandEntities, listState)) {
    return null;
  }

  return {
    templateId: stateId,
    shouldScroll: true,
    error: undefined,
    shouldUpdateUrl: true,
  };
}

function tryResolveFromActive(
  activeId: string | undefined,
  cardList: InspectionTemplateItemModel[],
  zustandEntities: InspectionTemplateModel[],
  listState: EntityState
): DetermineActiveTemplateResult | null {
  if (!activeId || !isTemplateKnown(activeId, cardList, zustandEntities, listState)) {
    return null;
  }

  return {
    templateId: activeId,
    shouldScroll: false,
    error: undefined,
    shouldUpdateUrl: false,
  };
}

function getFirstTemplateOrError(
  cardList: InspectionTemplateItemModel[]
): DetermineActiveTemplateResult {
  if (cardList.length === 0) {
    return {
      templateId: undefined,
      shouldScroll: false,
      error: NO_TEMPLATES_ERROR,
      shouldUpdateUrl: false,
    };
  }

  return {
    templateId: cardList[0]._id,
    shouldScroll: true,
    error: undefined,
    shouldUpdateUrl: false,
  };
}

export function determineActiveTemplate(
  urlId: string | undefined,
  stateId: string | undefined,
  cardList: InspectionTemplateItemModel[],
  activeId: string | undefined,
  zustandEntities: InspectionTemplateModel[],
  listState: EntityState
): DetermineActiveTemplateResult {
  return (
    tryResolveFromUrl(urlId, cardList, zustandEntities, listState) ??
    tryResolveFromState(stateId, cardList, zustandEntities, listState) ??
    tryResolveFromActive(activeId, cardList, zustandEntities, listState) ??
    getFirstTemplateOrError(cardList)
  );
}

export function updateTemplateUrl(
  navigate: NavigateFunction,
  projectId: string,
  templateId: string,
  replace: boolean = false
): void {
  navigate(
    APP_PATHS.inspectionTemplate.single(projectId, templateId),
    {
      replace,
      state: undefined
    }
  );
}

export function setActiveTemplateWithScroll(
  templateId: string,
  activeRef: { current: string | undefined },
  setActive: (id?: string) => void,
  setCanScroll: (value: boolean) => void,
  setError: (error: TemplateListError | undefined) => void
): void {
  activeRef.current = templateId;
  setActive(templateId);
  setCanScroll(true);
  setError(undefined);
}
